import {Checkbox} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';

import {StudySearchItem} from './StudySearchInput';

export interface AutoLinkingStudy {
  id: string;
  name: string;
}

interface BaseAutoLinkingProps {
  // The study that items created in the entry will be auto-linked to
  study: AutoLinkingStudy;
  isChecked: boolean;
  isDisabled: boolean;
  onCheckedChange: (e: InputChangeEvent) => void;
}

// The auto-linking study is fixed and cannot be changed by the user.
interface LockedAutoLinkingProps extends BaseAutoLinkingProps {
  mode: 'locked';
}

// The user picks the auto-linking study from the selected studies via dropdown.
interface SelectableAutoLinkingProps extends BaseAutoLinkingProps {
  mode: 'selectable';
  onStudyChange: (studyId: string) => void;
}

export type AutoLinkingProps = LockedAutoLinkingProps | SelectableAutoLinkingProps;

export interface StudyAutoLinkingInputProps {
  selectedStudies: Array<StudySearchItem>;
  invalidItemNames: Array<string>;
  autoLinking: AutoLinkingProps;
  // The layout of the auto-linking study selection dropdown.
  // If 'inline', the dropdown is displayed inline with the checkbox. If 'stacked', the dropdown is displayed below the checkbox.
  // This is depedendant on the modal layout and how much space the dropdown needs.
  dropdownLayout?: 'inline' | 'stacked';
}

export default function StudyAutoLinkingInput({
  selectedStudies,
  invalidItemNames,
  autoLinking,
  dropdownLayout = 'inline',
}: StudyAutoLinkingInputProps): JSX.Element {
  const {study, isChecked, isDisabled, onCheckedChange} = autoLinking;
  if (autoLinking.mode === 'locked') {
    return (
      <div className={css(styles.autoLinkingCheckbox)}>
        <CheckboxInput
          id="study-auto-link-checkbox"
          label={
            <div>
              Automatically link items created in this entry to <strong>{study.name}</strong>
            </div>
          }
          disabled={isDisabled}
          checked={isChecked}
          onChange={onCheckedChange}
        />
      </div>
    );
  }

  const {onStudyChange} = autoLinking;
  const isMultipleSelectedItems = selectedStudies.length > 1;
  const filteredStudies = selectedStudies.filter(
    (selectedStudy) => !invalidItemNames.includes(selectedStudy.name)
  );
  return (
    <div
      className={css(
        dropdownLayout === 'inline' ? styles.inlineAutoLinkingSection : styles.stackedAutoLinkingSection
      )}
    >
      <Checkbox
        className={css(styles.autoLinkingCheckbox)}
        id="study-auto-link-checkbox"
        checked={isChecked}
        onChange={onCheckedChange}
        disabled={isDisabled}
        label={
          <>
            Automatically link items created in this entry to{' '}
            {!isMultipleSelectedItems && <strong>{study.name}</strong>}
          </>
        }
      />
      {isMultipleSelectedItems && (
        <span
          className={css(
            dropdownLayout === 'inline' ? styles.inlineAutoLinkingDropdown : styles.stackedAutoLinkingDropdown
          )}
        >
          <SelectDropdown
            ariaLabel="Auto-linking study dropdown"
            displayName={study.name}
            menu={
              <Menu value={study.id} width={Menu.WIDTHS.FIT_CONTENT} className={css(styles.autoLinkingMenu)}>
                {filteredStudies.map((selectableStudy) => (
                  <Option
                    key={selectableStudy.id}
                    value={selectableStudy.id}
                    onSelect={() => {
                      onStudyChange(selectableStudy.id);
                    }}
                  >
                    {selectableStudy.name}
                  </Option>
                ))}
              </Menu>
            }
          />
        </span>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  inlineAutoLinkingSection: {
    alignItems: 'flex-start',
    display: 'flex',
    marginTop: 8,
  },
  stackedAutoLinkingSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 8,
    maxWidth: '100%',
    rowGap: '4px',
    width: 'fit-content',
  },
  autoLinkingCheckbox: {
    marginTop: 5,
  },
  inlineAutoLinkingDropdown: {
    flex: 1,
    marginLeft: 4,
    minWidth: 0,
  },
  stackedAutoLinkingDropdown: {
    maxWidth: '100%',
    minWidth: '100%',
    width: 0,
  },
  autoLinkingMenu: {
    minWidth: '100%',
  },
});
